import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  History,
  CheckCircle2,
  XCircle,
  Loader2,
  PauseCircle,
  Package,
} from "lucide-react";

import { useFullbaseScans } from "@/hooks/useFullbase";
import type { FullbaseScan } from "@/api/fullbase";
import { formatDateTime } from "@/lib/datetime";
import { tp } from "@/lib/plural";

interface ScanHistoryProps {
  projectId: string;
  /** "dark" pro hero, "light" (default) pra fundos brancos. */
  triggerTone?: "light" | "dark";
  /** Varredura atualmente selecionada (snapshot). null = consolidado. */
  selectedScanId?: string | null;
  /** Seleciona uma varredura pra ver o snapshot (null limpa). */
  onSelectScan?: (jobId: string | null) => void;
}

function formatDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return "-";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return "<1s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
}

const STATUS_CONFIG: Record<
  string,
  { icon: typeof CheckCircle2; color: string; bg: string; label: string }
> = {
  completed: {
    icon: CheckCircle2,
    color: "text-emerald-500",
    bg: "bg-emerald-500/15 border-emerald-500/30",
    label: "Completed",
  },
  failed: {
    icon: XCircle,
    color: "text-red-500",
    bg: "bg-red-500/15 border-red-500/30",
    label: "Failed",
  },
  cancelled: {
    icon: XCircle,
    color: "text-muted-foreground",
    bg: "bg-muted border-border",
    label: "Canceled",
  },
  paused: {
    icon: PauseCircle,
    color: "text-amber-500",
    bg: "bg-amber-500/15 border-amber-500/30",
    label: "Paused",
  },
};

const RUNNING = {
  icon: Loader2,
  color: "text-blue-500",
  bg: "bg-blue-500/15 border-blue-500/30",
  label: "Running",
};

function cfgFor(status: string) {
  return STATUS_CONFIG[status] ?? RUNNING;
}

/** Histórico de varreduras da base (full-base). Cada item é um SCAN lógico
 * (1 job), com escopo/objetos/custo/tempo agregados — em vez das execuções por
 * fase (extraction/analysis/synthesis), que poluíam o histórico genérico. */
export function ScanHistory({
  projectId,
  triggerTone = "light",
  selectedScanId = null,
  onSelectScan,
}: ScanHistoryProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const {
    data: scans = [],
    isLoading,
    isError,
    refetch,
  } = useFullbaseScans(open ? projectId : undefined);

  const handleSelect = (jobId: string) => {
    onSelectScan?.(jobId);
    setOpen(false); // fecha o drawer ao escolher a varredura
  };

  const triggerClass =
    triggerTone === "dark"
      ? "h-[30px] bg-white/[0.08] hover:bg-white/[0.14] text-white border border-white/[0.18] gap-1.5 px-3 text-[11px] font-semibold"
      : "h-8 text-xs gap-1.5";
  const triggerVariant = triggerTone === "dark" ? "ghost" : "outline";

  const completedCount = scans.filter((s) => s.status === "completed").length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant={triggerVariant} size="sm" className={triggerClass}>
          <History className="h-3.5 w-3.5" />
          {t("Scans")}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[440px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t("Scan history")}
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? t("Loading scans...")
              : tp(
                  t,
                  scans.length,
                  "{{done}} completed of {{n}} scan",
                  "{{done}} completed of {{n}} scans",
                  { done: completedCount },
                )}
          </p>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-4 -mx-6 px-6">
          <div className="space-y-3">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("Loading history...")}
              </div>
            )}
            {!isLoading && isError && (
              <div className="py-8 text-center text-xs text-muted-foreground space-y-3">
                <XCircle className="mx-auto h-5 w-5 text-red-500 opacity-70" />
                <p>{t("Could not load the scan history.")}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => refetch()}
                >
                  {t("Retry")}
                </Button>
              </div>
            )}
            {!isLoading && !isError && scans.length === 0 && (
              <p className="text-xs text-muted-foreground py-8 text-center">
                {t("No scans yet. Run the base from the panel above.")}
              </p>
            )}
            {!isLoading && !isError && scans.map((scan: FullbaseScan, idx: number) => {
              const cfg = cfgFor(scan.status);
              const StatusIcon = cfg.icon;
              const runNumber = scans.length - idx;
              const cost = scan.actual_cost_usd ?? 0;
              const isSelected = selectedScanId === scan.job_id;
              const clickable = Boolean(onSelectScan);
              return (
                <div
                  key={scan.job_id}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  aria-pressed={clickable ? isSelected : undefined}
                  onClick={clickable ? () => handleSelect(scan.job_id) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSelect(scan.job_id);
                          }
                        }
                      : undefined
                  }
                  className={`rounded-lg border p-3 transition-colors ${
                    clickable ? "cursor-pointer hover:bg-muted/50" : ""
                  } ${
                    isSelected
                      ? "border-primary ring-1 ring-primary bg-primary/5"
                      : "border-border"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">
                        {t("Scan #{{n}}", { n: runNumber })}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${cfg.bg} ${cfg.color}`}
                      >
                        <StatusIcon
                          className={`h-3 w-3 mr-1 ${
                            cfg === RUNNING ? "animate-spin" : ""
                          }`}
                        />
                        {t(cfg.label)}
                      </Badge>
                      {idx === 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {t("Latest")}
                        </Badge>
                      )}
                      {isSelected && (
                        <Badge className="text-[10px]">{t("Viewing")}</Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
                    <Package className="h-3 w-3" />
                    {scan.package_filter ? scan.package_filter : t("Whole base")}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(scan.created_at)}
                  </p>

                  <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {t("{{n}} cataloged", { n: scan.cataloged ?? 0 })}
                    </span>
                    <span>{t("{{n}} in scope", { n: scan.total_discovered })}</span>
                    {cost > 0 && <span>US$ {cost.toFixed(2)}</span>}
                    <span>{formatDuration(scan.started_at, scan.finished_at)}</span>
                  </div>

                  {scan.status === "failed" && scan.error && (
                    <p className="text-[11px] text-red-400 mt-1.5 line-clamp-2">
                      {scan.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
